import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { formatMoney, formatShort } from '../../theme/chart';
import { mixWithWhite } from '../../utils/color';
import { parseLocalDate } from '../../utils/bills';
import { Goal } from '../../services/goalApi';
import { GoalOutlook, PACE_WINDOW_MONTHS, monthsFromNow } from './goalOutlook';
import { MonthColumn, PLANNED_MONTHS, fitColumns } from './goalMonths';
import { ChartLegend, LegendItem } from './ChartLegend';
import { SectionLabel } from './goalUi';

interface Props {
  goal: Goal;
  /** every column the goal has; the ones that fit the width are drawn */
  columns: MonthColumn[];
  outlook: GoalOutlook;
  /** an event goal's average deposit over the last months; leave out for habits */
  average?: number;
  /** an event goal's months to the target at that average */
  arrivalMonths?: number | null;
  color: string;
  currency: string;
  now?: Date;
}

/** The height of the bars area, in px. */
const BARS_HEIGHT = 128;
/** Below this width per column the labels collide, so older months give way. */
const MIN_COLUMN = 48;

/** The element's width, followed as it changes; 0 until measured (and in tests). */
const useWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
};

/**
 * Deposits per month as vertical bars. Past months in the goal's tint, the
 * current one in its full color. An event goal with a deadline adds the
 * months left, outlined at the deposit each one needs, and a dashed line at
 * its recent pace; a habit outlines the monthly target wherever a month fell
 * short. One sentence underneath says what the bars mean.
 */
export const GoalMonthlyBars: React.FC<Props> = ({
  goal,
  columns,
  outlook,
  average,
  arrivalMonths,
  color,
  currency,
  now = new Date(),
}) => {
  const { t, i18n } = useTranslation('planning');
  const locale = i18n.language;
  const [ref, width] = useWidth();

  const shown = width > 0 ? fitColumns(columns, width / MIN_COLUMN) : columns;
  const past = shown.filter((column) => column.kind !== 'planned');
  const planned = shown.filter((column) => column.kind === 'planned');
  const current = past[past.length - 1];

  const isHabit = goal.goalType === 'recurring';
  const monthlyTarget = isHabit ? Math.max(0, Number(goal.monthlyTarget ?? 0)) : 0;
  const paceLine = !isHabit && (average ?? 0) > 0 ? (average as number) : 0;
  const paceSpan = Math.min(PACE_WINDOW_MONTHS, past.length);

  // Above the tallest bar: room for the small words that sit over a column.
  const top = Math.max(...shown.map((column) => column.amount), monthlyTarget, paceLine, 1) * 1.2;
  const pct = (value: number) => `${(Math.min(Math.max(value, 0) / top, 1) * 100).toFixed(2)}%`;

  const money = (value: number) => formatMoney(value, currency, locale);
  const plain = (value: number) =>
    value < 10000 ? new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value) : formatShort(value, locale);
  const shortMonth = (date: Date) => date.toLocaleDateString(locale, { month: 'short' }).replace('.', '');
  const longMonth = (date: Date) =>
    date.getFullYear() === now.getFullYear()
      ? date.toLocaleDateString(locale, { month: 'long' })
      : date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  // Words over the planned columns: where the deadline falls and, when the
  // pace beats it, the month the goal gets there.
  const markers = new Map<string, { text: string; tone: string }>();
  if (outlook?.kind === 'path') {
    const deadlineIndex = outlook.monthsToDeadline <= PLANNED_MONTHS ? outlook.monthsToDeadline - 1 : -1;
    if (deadlineIndex >= 0 && planned[deadlineIndex]) {
      markers.set(planned[deadlineIndex].key, { text: t('goals.bars.deadline'), tone: tokens.ink3 });
    }
    if (!outlook.behind && arrivalMonths != null && arrivalMonths > 0) {
      const arrivalIndex = Math.ceil(arrivalMonths) - 1;
      if (arrivalIndex !== deadlineIndex && planned[arrivalIndex]) {
        markers.set(planned[arrivalIndex].key, { text: t('goals.bars.arrival'), tone: tokens.accent });
      }
    }
  }
  if (monthlyTarget > 0 && current && current.amount < monthlyTarget) {
    markers.set(current.key, { text: t('goals.bars.missing', { amount: plain(monthlyTarget - current.amount) }), tone: tokens.accent });
  }

  const label = [
    t('goals.bars.title'),
    past.map((column) => `${longMonth(column.date)} ${money(column.amount)}`).join(', '),
    planned.length
      ? t('goals.bars.plannedLabel', { list: planned.map((column) => `${longMonth(column.date)} ${money(column.amount)}`).join(', ') })
      : '',
  ]
    .filter(Boolean)
    .join('. ');

  const legend: LegendItem[] = [{ key: 'deposits', label: t('goals.bars.deposits'), swatch: 'block', color }];
  if (paceLine > 0) legend.push({ key: 'pace', label: t('goals.bars.pace'), swatch: 'dash', color: tokens.ink });
  if (planned.length) legend.push({ key: 'planned', label: t('goals.bars.planned'), swatch: 'outline', color });
  if (monthlyTarget > 0) legend.push({ key: 'target', label: t('goals.bars.monthTarget', { amount: money(monthlyTarget) }), swatch: 'outline', color });

  let insight: React.ReactNode = null;
  const strong = { b: <strong /> };
  if (monthlyTarget > 0 && current) {
    const closed = past.slice(0, -1);
    const met = closed.filter((column) => column.amount >= monthlyTarget).length;
    insight = (
      <>
        {closed.length > 0 && (
          <Trans t={t} i18nKey='goals.bars.habitMet' count={closed.length} values={{ met }} components={strong} />
        )}
        {closed.length > 0 && ' '}
        {current.amount >= monthlyTarget
          ? t('goals.bars.habitMonthDone', { month: longMonth(current.date) })
          : t('goals.bars.habitMissing', { amount: money(monthlyTarget - current.amount), month: longMonth(current.date) })}
      </>
    );
  } else if (outlook?.kind === 'path' && outlook.needed > 0) {
    const deadlineMonth = goal.targetDate ? longMonth(parseLocalDate(goal.targetDate)) : '';
    if (outlook.behind) {
      insight =
        outlook.average > 0 ? (
          <Trans
            t={t}
            i18nKey='goals.bars.insightBehind'
            values={{ needed: money(outlook.needed), month: deadlineMonth, extra: money(outlook.needed - outlook.average) }}
            components={strong}
          />
        ) : (
          <Trans t={t} i18nKey='goals.bars.insightBehindNoPace' values={{ needed: money(outlook.needed), month: deadlineMonth }} components={strong} />
        );
    } else if (outlook.arrivalMonths != null) {
      insight = (
        <Trans
          t={t}
          i18nKey='goals.bars.insightOnTrack'
          values={{ average: money(outlook.average), needed: money(outlook.needed), arrival: longMonth(monthsFromNow(outlook.arrivalMonths, now)) }}
          components={strong}
        />
      );
    }
  } else if (!isHabit && !goal.isAchieved && arrivalMonths != null && arrivalMonths > 0) {
    insight = (
      <Trans t={t} i18nKey='goals.bars.insightArrival' values={{ arrival: longMonth(monthsFromNow(arrivalMonths, now)) }} components={strong} />
    );
  }

  const pastTint = mixWithWhite(color, 0.6);
  const plannedTint = mixWithWhite(color, 0.93);
  const barWidth = 'min(36px, 64%)';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <SectionLabel>{t('goals.bars.title')}</SectionLabel>

      <Box ref={ref} role='img' aria-label={label} sx={{ position: 'relative', display: 'flex' }}>
        {shown.map((column) => {
          const isCurrent = column.kind === 'current';
          const isPlanned = column.kind === 'planned';
          const shortfall = monthlyTarget > 0 && !isPlanned && column.amount < monthlyTarget;
          const marker = markers.get(column.key);
          const markerBase = isPlanned ? column.amount : shortfall ? monthlyTarget : column.amount;
          const isLast = column === shown[shown.length - 1];
          return (
            <Box key={column.key} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', width: '100%', height: BARS_HEIGHT, borderBottom: `1px solid ${tokens.line}` }}>
                {shortfall && (
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: barWidth,
                      height: pct(monthlyTarget),
                      border: `1.5px dashed ${color}`,
                      borderRadius: '6px',
                      bgcolor: isCurrent ? plannedTint : 'transparent',
                      boxSizing: 'border-box',
                    }}
                  />
                )}
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: barWidth,
                    height: pct(column.amount),
                    minHeight: column.amount > 0 ? 3 : 0,
                    borderRadius: '6px',
                    boxSizing: 'border-box',
                    bgcolor: isPlanned ? plannedTint : isCurrent ? color : pastTint,
                    border: isPlanned ? `1.5px dashed ${color}` : 'none',
                  }}
                />
                {marker && (
                  <Box
                    component='span'
                    sx={{
                      position: 'absolute',
                      bottom: `calc(${pct(markerBase)} + 4px)`,
                      ...(isLast && marker.text.length > 6
                        ? { right: 0 }
                        : { left: '50%', transform: 'translateX(-50%)' }),
                      fontSize: 11,
                      fontWeight: 700,
                      color: marker.tone,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {marker.text}
                  </Box>
                )}
              </Box>
              <Box component='span' sx={{ mt: 1, fontSize: 12, lineHeight: 1.4, color: isCurrent ? tokens.ink : tokens.ink3, fontWeight: isCurrent ? 700 : 400 }}>
                {shortMonth(column.date)}
              </Box>
              <Box
                component='span'
                sx={{
                  fontSize: 12,
                  lineHeight: 1.4,
                  fontWeight: isCurrent ? 800 : 600,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  color: isPlanned || (isCurrent && monthlyTarget > 0) ? color : tokens.ink,
                }}
              >
                {plain(column.amount)}
              </Box>
            </Box>
          );
        })}

        {paceLine > 0 && paceSpan > 0 && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              left: `${((past.length - paceSpan) / shown.length) * 100}%`,
              width: `${(paceSpan / shown.length) * 100}%`,
              height: BARS_HEIGHT,
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: pct(paceLine), borderTop: `1.5px dashed ${tokens.ink}` }} />
          </Box>
        )}
      </Box>

      <ChartLegend items={legend} />
      {insight && (
        <Box component='p' sx={{ m: 0, fontSize: 13.5, color: tokens.ink2, '& strong': { color: tokens.ink, fontWeight: 700 } }}>
          {insight}
        </Box>
      )}
    </Box>
  );
};
